<template>
  <SListItem
    icon="icon-calendar"
    icon-color="gray-300"
    :data-cy="!isMarkedAsRead ? `notification-centre-list-item-${notification.uuid}-unread` : `notification-centre-list-item-${notification.uuid}-read`"
    :datetime="this.notification.created_at"
    :datetime-format="isToday ? 'dynamicDateTime' : 'dateMonthShort'"
  >
    <template #icon-additional-top>
      <span v-if="!isMarkedAsRead" class="red-unread-dot d-flex rounded-circle bg-primary" />
    </template>

    <template #item-title>
      <b-button
        v-if="notification.content_rendered"
        :id="`notification-centre-list-item-${notification.uuid}`"
        :data-cy="`notification-centre-list-item-${notification.uuid}`"
        variant="transparent"
        class="reset-button sdui-list-item__click-area"
        @click="executeStaticListItemAction"
      >
        <strong class="font-weight-bold" data-test="notification-centre-list-item-title">
          {{ notification.content_rendered }}
        </strong>
      </b-button>
    </template>

    <template #description>
      <p
        v-if="descriptionByType"
        class="gray-400 font-weight-normal m-0 nano text-muted only-two-lines"
        data-test="notification-centre-list-item-description"
      >
        {{ descriptionByType }}
      </p>
    </template>

    <template #right-action>
      <SDropDown
        class="gray-400 p-0"
        variant="transparent"
        no-caret
        :data-cy="`notification-drop-down-${notification.uuid}`"
      >
        <template #button-content>
          <span class="sr-only">{{ $t('notification_centre.dropdown_button') }}</span>
          <sdui-icon class="icon-more fz-base" />
        </template>
        <b-dropdown-item-button
          v-if="!isMarkedAsRead"
          :data-cy="`mark-notification-read-${notification.uuid}`"
          :disabled="$wait.waiting('mark-as-read')"
          @click.stop="markRead"
        >
          <sdui-icon class="icon-eye-o pr-3" />
          <span>{{ $t('notification_centre.mark_read') }}</span>
        </b-dropdown-item-button>
        <b-dropdown-item-button
          v-else
          :data-cy="`mark-notification-unread-${notification.uuid}`"
          :disabled="$wait.waiting('mark-as-unread')"
          @click.stop="markUnread"
        >
          <sdui-icon class="icon-hide-o pr-3" />
          <span>{{ $t('notification_centre.mark_unread') }}</span>
        </b-dropdown-item-button>
      </SDropDown>
    </template>
  </SListItem>
</template>

<script>
import { Message } from 'src/store/models/channel/index.js'
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import notificationCentreMixin from 'src/mixins/channels/notificationCentreMixin.js'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'

export default {
  name: 'NotificationCentreListItem',
  mixins: [notificationCentreMixin],
  props: {
    notification: {
      type: Object,
      required: true,
    },
  },

  data: () => ({}),

  computed: {
    descriptionByType() {
      // 'notification.target_type' indicates the source of the notification
      // For example "event" is always a calendarEvent notification, so we know how to construct the description
      if (!this.notification.target_snapshot) return ''

      if (this.isCalendarEventNotification) {
        return this.$t('notification_centre.calendar_event.description', {
          eventDescription: this.notification.target_snapshot?.name,
          eventCreator: this.notification.target_snapshot?.user?.meta?.displayname,
        })
      }

      // All other/unknown target_types will only display the title until we know how to handle them
      return ''
    },

    isMarkedAsRead() {
      return this.notification.is_read
    },

    isToday() {
      return this.$moment(this.notification?.created_at).isSame(new Date(), 'day')
    },
  },

  emits: ['list-item-clicked'],

  methods: {
    executeStaticListItemAction() {
      if (!this.staticListItemAction) return
      this.$emit('list-item-clicked')

      // Independent from the action, we will mark the notification as seen as soon the user clicks on it
      // (intentionally not pre-fixed with 'await' to not block the user and execute it in the background)
      !this.isMarkedAsRead && this.markRead()
    },

    markRead: waitFor('mark-as-read', async function () {
      await Message.markNotificationAsRead(this.notification.uuid)
      clyTracking.trackNotification().action.markAs({ read: true })
    }),

    markUnread: waitFor('mark-as-unread', async function () {
      await Message.markNotificationAsUnread(this.notification.uuid)
      clyTracking.trackNotification().action.markAs({ read: false })
    }),
  },
}
</script>

<style scoped lang="scss">
:deep(.sdui-list-item__icon-additional) .red-unread-dot {
  border: 2px solid $color-white;
  width: 12px;
  height: 12px;
}

:deep(.icon-height-limit > *),
.icon-height-limit {
  height: 36px;
  min-height: auto !important;
  width: 36px !important;
}

.only-two-lines {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
